import React, { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { Capacitor } from '@capacitor/core'
import { Auth0Native } from '../components/Auth0Native'
import Config from '../services/config'
import { useFeaturesStore } from '../store/featuresStore'

interface AuthUser {
  id: string
  email: string
  name: string
  namespace?: {
    id: string
    name: string
    title: string
    description: string
  } | undefined
}

interface AuthContextType {
  user: AuthUser | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  login: () => Promise<void>
  logout: () => Promise<void>
  getAccessToken: () => Promise<string | undefined>
  showWebview: boolean
  setShowWebview: (show: boolean) => void
}

const AuthContext = createContext<AuthContextType | null>(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const {
    user: auth0User,
    isAuthenticated: auth0IsAuthenticated,
    isLoading: auth0IsLoading,
    logout: auth0Logout,
    loginWithRedirect,
    getAccessTokenSilently,
    error,
  } = useAuth0()

  // Check for stored tokens immediately on Android
  const getStoredAuthState = () => {
    if (Capacitor.isNativePlatform()) {
      console.log('📱 Checking for stored tokens on mount...')
      const scope = 'openid profile email read:devices read:device_data read:locations read:events read:alarms'
      const storageKey = `@@auth0spajs@@::${Config.AUTH0_CLIENT_ID}::${Config.AUTH0_AUDIENCE}::${scope}`
      console.log('📱 Looking for key:', storageKey)
      const storedData = localStorage.getItem(storageKey)
      console.log('📱 Found stored data:', !!storedData)

      if (storedData) {
        try {
          const parsed = JSON.parse(storedData)
          console.log('📱 Parsed token data, has body:', !!parsed.body, 'has id_token:', !!parsed.body?.id_token)
          if (parsed.body?.id_token) {
            // Check if token is not expired
            const currentTime = Math.floor(Date.now() / 1000)
            console.log('📱 Token expires at:', parsed.expiresAt, 'current time:', currentTime, 'expired:', parsed.expiresAt <= currentTime)
            if (parsed.expiresAt && parsed.expiresAt > currentTime) {
              // Decode the ID token to get user info
              try {
                const idTokenPayload = parsed.body.id_token.split('.')[1]
                // Handle URL-safe base64
                const base64 = idTokenPayload.replace(/-/g, '+').replace(/_/g, '/')
                const padded = base64 + '='.repeat((4 - base64.length % 4) % 4)
                const decodedUser = JSON.parse(atob(padded))

                console.log('📱 Found valid stored tokens, user email:', decodedUser.email)
                return {
                  isAuthenticated: true,
                  user: decodedUser
                }
              } catch (decodeError) {
                console.error('📱 Error decoding initial stored token:', decodeError)
                return null
              }
            } else {
              console.log('📱 Found expired tokens, clearing them')
              localStorage.removeItem(storageKey)
            }
          } else {
            console.log('📱 Stored data missing id_token')
          }
        } catch (e) {
          console.error('📱 Error parsing stored tokens:', e)
        }
      } else {
        console.log('📱 No stored tokens found')
      }
    }
    return null
  }

  // Custom authentication state for Android WebView
  const [customAuthState, setCustomAuthState] = useState<{
    isAuthenticated: boolean
    user: any
  } | null>(getStoredAuthState)

  // Check for manually stored tokens (for Android) - now only for monitoring new tokens
  useEffect(() => {
    if (Capacitor.isNativePlatform() && !customAuthState) {
      let interval: NodeJS.Timeout | null = null

      const checkStoredTokens = () => {
        const scope = 'openid profile email read:devices read:device_data read:locations read:events read:alarms'
        const storageKey = `@@auth0spajs@@::${Config.AUTH0_CLIENT_ID}::${Config.AUTH0_AUDIENCE}::${scope}`
        const storedData = localStorage.getItem(storageKey)

        if (storedData) {
          try {
            const parsed = JSON.parse(storedData)
            if (parsed.body?.id_token) {
              // Check if token is not expired
              const currentTime = Math.floor(Date.now() / 1000)
              if (parsed.expiresAt && parsed.expiresAt > currentTime) {
                // Decode the ID token to get user info
                try {
                  const idTokenPayload = parsed.body.id_token.split('.')[1]
                  // Handle URL-safe base64
                  const base64 = idTokenPayload.replace(/-/g, '+').replace(/_/g, '/')
                  const padded = base64 + '='.repeat((4 - base64.length % 4) % 4)
                  const decodedUser = JSON.parse(atob(padded))

                  console.log('📱 Found stored tokens, setting custom auth state')
                  setCustomAuthState({
                    isAuthenticated: true,
                    user: decodedUser
                  })

                  // Stop checking once we found tokens
                  if (interval) {
                    clearInterval(interval)
                    interval = null
                  }
                } catch (decodeError) {
                  console.error('📱 Error decoding stored token:', decodeError)
                  // Don't keep trying if decoding fails
                  if (interval) {
                    clearInterval(interval)
                    interval = null
                  }
                }
              }
            }
          } catch (e) {
            console.error('📱 Error parsing stored tokens:', e)
          }
        }
      }

      // Check periodically in case tokens are added later (but not too often)
      interval = setInterval(checkStoredTokens, 500)

      // Stop checking after 10 seconds
      setTimeout(() => {
        if (interval) {
          clearInterval(interval)
        }
      }, 10000)

      // Cleanup
      return () => {
        if (interval) {
          clearInterval(interval)
        }
      }
    }
  }, [customAuthState])

  // Use custom auth state if available, otherwise use Auth0's state
  const isAuthenticated = customAuthState?.isAuthenticated ?? auth0IsAuthenticated
  // If we have custom auth state, we're not loading (tokens were found)
  // If we don't have custom auth state but are on native platform, check if Auth0 is still loading
  const isLoading = Capacitor.isNativePlatform() ? (customAuthState ? false : auth0IsLoading) : auth0IsLoading
  const currentUser = customAuthState?.user ?? auth0User

  // Debug Auth0 state changes
  React.useEffect(() => {
    console.log('🔐 Auth state:', {
      isAuthenticated,
      isLoading,
      hasCustomAuthState: !!customAuthState,
      customAuthUser: customAuthState?.user?.email || 'None',
      auth0User: auth0User ? 'Present' : 'None',
      error: error ? error.message : 'None',
      isNativePlatform: Capacitor.isNativePlatform()
    })
  }, [isAuthenticated, isLoading, auth0User, error, customAuthState])

  const [namespaceData, setNamespaceData] = useState<{
    id: string;
    name: string;
    title: string;
    description: string;
  } | null>(null)
  const [showWebview, setShowWebview] = useState(false)

  // Get namespace from Auth0 user metadata (same as frontend)
  useEffect(() => {
    if (isAuthenticated && currentUser && !namespaceData) {
      console.log('🔐 ==================== NAMESPACE DEBUG ====================')
      console.log('🔐 Getting namespace from user metadata...')
      console.log('🔐 Current user object:', JSON.stringify(currentUser, null, 2))
      console.log('🔐 User metadata:', currentUser.user_metadata)
      console.log('🔐 User metadata type:', typeof currentUser.user_metadata)
      console.log('🔐 User metadata keys:', currentUser.user_metadata ? Object.keys(currentUser.user_metadata) : 'undefined')
      console.log('🔐 org_id from token:', currentUser.org_id)
      console.log('🔐 All user properties:', Object.keys(currentUser))

      // Check for namespace in user metadata (same as frontend)
      if (currentUser.user_metadata?.ns_id && currentUser.user_metadata?.ns_name) {
        const namespaceFromAuth0 = {
          id: currentUser.user_metadata.ns_id,
          name: currentUser.user_metadata.ns_name,
          title: currentUser.user_metadata.ns_title || currentUser.user_metadata.ns_name,
          description: currentUser.user_metadata.ns_description || 'Primary namespace from Auth0'
        }

        setNamespaceData(namespaceFromAuth0)
        console.log('🔐 ✅ Retrieved namespace from Auth0 metadata:', namespaceFromAuth0)
      } else {
        console.warn('🔐 ⚠️ No namespace found in user metadata - WebSocket will not connect')
        console.warn('🔐 Available metadata fields:', currentUser.user_metadata ? Object.keys(currentUser.user_metadata) : 'undefined')
        console.warn('🔐 Full user object for debugging:', currentUser)
        setNamespaceData(null)
      }
      console.log('🔐 ======================================================')
    }
  }, [isAuthenticated, currentUser, namespaceData])

  // Load features when authenticated with namespace
  useEffect(() => {
    if (isAuthenticated && namespaceData?.id && currentUser) {
      console.log('🎯 Loading features for namespace:', namespaceData.id)
      getAccessToken().then(token => {
        if (token) {
          const { loadFeatures } = useFeaturesStore.getState()
          loadFeatures(namespaceData.id, token)
        }
      })
    }
  }, [isAuthenticated, namespaceData?.id, currentUser])
  
  const user: AuthUser | null = currentUser ? {
    id: currentUser.sub || '',
    email: currentUser.email || '',
    name: currentUser.name || '',
    namespace: namespaceData || undefined,
  } : null
  
  console.log('🔐 Auth0 User Data:', auth0User)
  console.log('🔐 Namespace from Strata47:', namespaceData)
  console.log('🔐 Final User:', user)

  const login = async (): Promise<void> => {
    console.log('📱 Initiating login')

    if (Capacitor.isNativePlatform()) {
      // For mobile, use the custom webview approach
      console.log('📱 Using webview for mobile Auth0 authentication')

      // Don't clear tokens here - they might be valid
      // They'll be overwritten if new authentication succeeds

      setShowWebview(true)
    } else {
      // For web, use the standard Auth0 SDK redirect
      console.log('📱 Using standard Auth0 redirect for web')
      await loginWithRedirect({
        authorizationParams: {
          redirect_uri: 'https://omniiq-mobile-staging.pages.dev/callback'
        }
      })
    }
  }

  const handleWebviewAuthSuccess = (tokens: any) => {
    console.log('📱 Webview authentication successful:', tokens)

    // Store tokens in localStorage for Auth0React to pick up - use exact same key format
    const scope = 'openid profile email read:devices read:device_data read:locations read:events read:alarms'
    const storageKey = `@@auth0spajs@@::${Config.AUTH0_CLIENT_ID}::${Config.AUTH0_AUDIENCE}::${scope}`
    console.log('📱 Using storage key:', storageKey)

    // Parse JWT token for Auth0React compatibility
    let webviewUserFromToken = null
    try {
      const idTokenPayload = tokens.id_token.split('.')[1]
      // Handle URL-safe base64
      const base64 = idTokenPayload.replace(/-/g, '+').replace(/_/g, '/')
      const padded = base64 + '='.repeat((4 - base64.length % 4) % 4)
      const decodedPayload = JSON.parse(atob(padded))
      webviewUserFromToken = decodedPayload
      console.log('📱 Successfully decoded ID token for user:', decodedPayload.email)
    } catch (error) {
      console.error('📱 Error decoding ID token:', error)
      // Continue anyway - authentication succeeded even if we can't decode
    }

    // Format tokens exactly as Auth0React expects (with refresh token)
    const webviewTokenPayload = {
      body: {
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token || null,
        id_token: tokens.id_token,
        scope: tokens.scope,
        expires_in: tokens.expires_in,
        token_type: tokens.token_type,
        decodedToken: {
          claims: webviewUserFromToken,
          user: webviewUserFromToken
        }
      },
      expiresAt: Math.floor(Date.now() / 1000) + tokens.expires_in,
    }
    localStorage.setItem(storageKey, JSON.stringify(webviewTokenPayload))

    // Set custom auth state immediately
    if (webviewUserFromToken) {
      setCustomAuthState({
        isAuthenticated: true,
        user: webviewUserFromToken
      })
    }

    // Close webview
    setShowWebview(false)

    // No need to navigate or reload - the state change will trigger re-render
    console.log('📱 Authentication successful, auth state updated')
  }

  const handleWebviewAuthError = (error: string) => {
    console.error('📱 Webview authentication failed:', error)
    setShowWebview(false)
    // Could show an error message to the user here
  }

  const handleWebviewClose = () => {
    console.log('📱 Webview authentication cancelled')
    setShowWebview(false)
  }

  const logout = async (): Promise<void> => {
    console.log('📱 Logging out...')

    // Clear custom auth state if present
    setCustomAuthState(null)

    // Clear features
    const { clearFeatures } = useFeaturesStore.getState()
    clearFeatures()

    // Clear all Auth0 related items from localStorage
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && (key.includes('@@auth0spajs@@') || key.includes('auth0'))) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(key => {
      console.log('📱 Removing auth key:', key)
      localStorage.removeItem(key)
    })

    // For native platforms, just clear local state
    if (Capacitor.isNativePlatform()) {
      console.log('📱 Native logout - tokens cleared')
      // Don't call Auth0 logout on native - just clear tokens locally
      // The app will redirect to login automatically when isAuthenticated becomes false
    } else {
      // For web, use the standard Auth0 logout
      await auth0Logout({
        logoutParams: {
          returnTo: window.location.origin
        }
      })
    }
  }

  const getAccessToken = async (): Promise<string | undefined> => {
    try {
      // If we have custom auth state, get token from localStorage
      if (customAuthState?.isAuthenticated) {
        const scope = 'openid profile email read:devices read:device_data read:locations read:events read:alarms'
        const storageKey = `@@auth0spajs@@::${Config.AUTH0_CLIENT_ID}::${Config.AUTH0_AUDIENCE}::${scope}`
        const storedData = localStorage.getItem(storageKey)

        if (storedData) {
          const parsed = JSON.parse(storedData)
          if (parsed.body?.access_token) {
            return parsed.body.access_token
          }
        }
      }

      // Otherwise try Auth0's method
      if (auth0IsAuthenticated) {
        return await getAccessTokenSilently()
      }
      return undefined
    } catch (error) {
      console.error('Failed to get access token:', error)

      // If the error is about missing refresh token, logout and redirect to login
      if (error instanceof Error && error.message.includes('Missing Refresh Token')) {
        console.log('🔐 Missing refresh token, logging out...')
        await logout()
      }

      return undefined
    }
  }

  const value: AuthContextType = {
    user,
    token: null, // Token is handled internally by Auth0
    isAuthenticated,
    isLoading, // Remove namespace loading since we simplified it
    login,
    logout,
    getAccessToken,
    showWebview,
    setShowWebview,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
      {showWebview && (
        <Auth0Native
          isVisible={showWebview}
          onAuthSuccess={handleWebviewAuthSuccess}
          onAuthError={handleWebviewAuthError}
          onClose={handleWebviewClose}
        />
      )}
    </AuthContext.Provider>
  )
}